function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, MenuItem } from '@mui/material';
import { COLORS, SIZES, Typography } from '@fupa/fupa-uikit';
import styled from 'styled-components';
import { DataEntry, Label } from 'app/components/match/liveResult/CommonRowStyles';
import { LoadingButton } from '@mui/lab';
import { Controller, useForm } from 'react-hook-form';
import { isNumber } from 'app/helpers/ValidationRules';
import { CustomNumberTextField, CustomTextField } from 'app/components/match/liveResult/CustomNumberTextField';
import PlayerSelect from 'app/components/match/liveResult/updateScorer/PlayerSelect';
import { goalType } from 'app/helpers/goalType.const';
import DeleteScorerButton from 'app/components/match/liveResult/updateScorer/DeleteScorerButton';
import { useStoreSofttickerFormDataInSessionStorage } from 'app/hooks/useStoreSofttickerFormDataInSessionStorage';
import is from '@sindresorhus/is';
const FormContainer = styled.form.withConfig({
  componentId: "sc-1orwzi5-0"
})(["display:grid;gap:", ";grid-template-columns:repeat(3,auto);grid-template-rows:1fr repeat(4,64px) 20px auto;height:30rem;"], SIZES['16']);
const GridRow = styled.div.withConfig({
  componentId: "sc-1orwzi5-1"
})(["grid-column:span 3;display:grid;grid-template-columns:repeat(2,1fr) 40px;column-gap:", ";"], SIZES['16']);
const GridElement = styled.div.withConfig({
  componentId: "sc-1orwzi5-2"
})(["width:100%;grid-column:span 2;"]);
const SharedErrorContainer = styled.div.withConfig({
  componentId: "sc-1orwzi5-3"
})(["grid-column:span 3;min-height:", ";display:flex;flex-direction:column;"], SIZES['20']);
const ErrorTypography = styled(Typography).withConfig({
  componentId: "sc-1orwzi5-4"
})(["&&&{color:", ";}"], COLORS.red);
const ActionContainer = styled.div.withConfig({
  componentId: "sc-1orwzi5-5"
})(["padding-top:", ";grid-column:span 3;display:flex;justify-content:space-between;"], SIZES['8']);
const getOtherTeam = (teams, teamSlug) => {
  const slug = Object.keys(teams).find(key => key !== teamSlug);
  if (!slug) {
    return [];
  }
  return teams[slug];
};
const hasGoals = goals => {
  if (is.number(goals)) {
    return goals > 0;
  }
  if (is.string(goals)) {
    const number = parseInt(goals, 10);
    return number > 0;
  }
  return false;
};
const labelAssistant = 'Vorlagengeber';
const labelFouled = 'gefoulter Spieler';
const assistantLabel = {
  goal_shoot: labelAssistant,
  goal_free_kick: labelFouled,
  goal_freekick: labelFouled,
  goal_headed: labelAssistant,
  goal_distance_shoot: labelAssistant,
  goal_scrounger: labelAssistant,
  penalty_foul: labelFouled,
  penalty_hand: null,
  penalty_shootout: null,
  goal_own_goal: null
};
const defaultInitData = {
  homeGoals: null,
  awayGoals: null,
  minute: null,
  category: 'goal_shoot',
  team: '',
  scorer: '',
  assistant: ''
};
export default function UpdateScorerForm(_ref) {
  var _initData$homeGoals, _initData$awayGoals, _initData$minute, _initData$category, _initData$team, _initData$scorer, _initData$assistant, _initData$homeGoals2, _initData$awayGoals2, _initData$category2, _errors$category$mess, _errors$category, _initData$team2, _initData$scorer2, _initData$assistant2;
  let {
    onClose,
    match,
    onDelete,
    initData,
    matchEventId,
    players,
    onSubmit,
    submissionLoading,
    submissionError
  } = _ref;
  const [deletionError, setDeletionError] = useState(null);
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isDirty
    },
    setValue,
    setError,
    watch,
    control,
    getValues
  } = useForm({
    defaultValues: {
      homeGoals: (_initData$homeGoals = initData === null || initData === void 0 ? void 0 : initData.homeGoals) !== null && _initData$homeGoals !== void 0 ? _initData$homeGoals : defaultInitData.homeGoals,
      awayGoals: (_initData$awayGoals = initData === null || initData === void 0 ? void 0 : initData.awayGoals) !== null && _initData$awayGoals !== void 0 ? _initData$awayGoals : defaultInitData.awayGoals,
      minute: (_initData$minute = initData === null || initData === void 0 ? void 0 : initData.minute) !== null && _initData$minute !== void 0 ? _initData$minute : defaultInitData.minute,
      category: (_initData$category = initData === null || initData === void 0 ? void 0 : initData.category) !== null && _initData$category !== void 0 ? _initData$category : defaultInitData.category,
      team: (_initData$team = initData === null || initData === void 0 ? void 0 : initData.team) !== null && _initData$team !== void 0 ? _initData$team : defaultInitData.team,
      scorer: (_initData$scorer = initData === null || initData === void 0 ? void 0 : initData.scorer) !== null && _initData$scorer !== void 0 ? _initData$scorer : defaultInitData.scorer,
      assistant: (_initData$assistant = initData === null || initData === void 0 ? void 0 : initData.assistant) !== null && _initData$assistant !== void 0 ? _initData$assistant : defaultInitData.assistant
    },
    mode: 'onTouched',
    reValidateMode: 'onChange'
  });
  const getSharedError = useCallback(errorObjects => {
    const firstError = errorObjects.find(error => error);
    return firstError ? /*#__PURE__*/React.createElement(ErrorTypography, {
      variant: "caption1"
    }, firstError.message) : null;
  }, [errors]);
  const initializeFormData = useCallback(data => {
    Object.keys(data).forEach(key => {
      setValue(key, data[key]);
    });
  }, [setValue]);
  useStoreSofttickerFormDataInSessionStorage('scorerFormData', getValues, initializeFormData);
  const team = watch('team');
  const category = watch('category');
  const homeGoals = watch('homeGoals');
  const awayGoals = watch('awayGoals');
  const isOwnGoal = useMemo(() => category === 'goal_own_goal', [category]);
  const scorer = watch('scorer');
  useEffect(() => {
    if (initData !== null && initData !== void 0 && initData.team) {
      return;
    }
    if (homeGoals === null || homeGoals === undefined || awayGoals === null || awayGoals === undefined) {
      return;
    }
    const homeTeamHasGoals = hasGoals(homeGoals);
    const awayTeamHasGoals = hasGoals(awayGoals);
    if (homeTeamHasGoals && !awayTeamHasGoals) {
      return setValue('team', 'homeTeam', {
        shouldValidate: true
      });
    }
    if (!homeTeamHasGoals && awayTeamHasGoals) {
      return setValue('team', 'awayTeam', {
        shouldValidate: true
      });
    }
    setValue('team', '');
  }, [initData, homeGoals, awayGoals]);
  const relevantPlayers = useMemo(() => {
    if (!players || !team) {
      return [];
    }
    const teamSlug = match[team].slug;
    return isOwnGoal ? getOtherTeam(players, teamSlug) : players[teamSlug];
  }, [isOwnGoal, players, team, match]);
  const relevantAssistants = useMemo(() => {
    if (!players || !team) {
      return [];
    }
    const teamSlug = match[team].slug;
    return players[teamSlug];
  }, [match, team, players]);
  useEffect(() => {
    if (isDirty) {
      setValue('scorer', '');
    }
  }, [relevantPlayers]);
  useEffect(() => {
    if (isDirty) {
      setValue('assistant', '');
    }
  }, [relevantAssistants]);
  const onValidationSuccess = async _ref2 => {
    let {
      category,
      minute,
      homeGoals,
      awayGoals,
      team,
      scorer,
      assistant
    } = _ref2;
    const data = {
      minute,
      homeGoals,
      awayGoals,
      teamId: match[team].id,
      scorerId: is.string(scorer) ? null : scorer,
      assistantId: is.string(assistant) ? null : assistant,
      type: category
    };
    onSubmit(data, setError);
  };
  return /*#__PURE__*/React.createElement(FormContainer, {
    "data-sentry-element": "FormContainer",
    "data-sentry-component": "UpdateScorerForm",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }, /*#__PURE__*/React.createElement(GridRow, {
    "data-sentry-element": "GridRow",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }, /*#__PURE__*/React.createElement(CustomNumberTextField, _extends({
    error: !!errors.minute,
    type: "number",
    variant: "outlined",
    label: "Spielminute",
    size: "small",
    inputProps: {
      inputMode: 'numeric'
    }
  }, register('minute', {
    required: 'Gib die Spielminute an.',
    validate: {
      isNumber
    },
    max: {
      value: 199,
      message: 'Es darf maximal Minute 199 angegeben werden.'
    },
    min: {
      value: 0,
      message: 'Es muss mindestens Minute 0 angegeben sein.'
    }
  }), {
    "data-sentry-element": "CustomNumberTextField",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  })), /*#__PURE__*/React.createElement(DataEntry, {
    justify: "center",
    "data-sentry-element": "DataEntry",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }, /*#__PURE__*/React.createElement(CustomNumberTextField, _extends({
    error: !!errors.homeGoals,
    type: "number",
    variant: "outlined",
    label: "Heim",
    size: "small",
    maxWidth: 52,
    textAlign: "center",
    inputProps: {
      inputMode: 'numeric'
    },
    InputLabelProps: {
      shrink: true
    }
  }, register('homeGoals', {
    required: 'Gib die Anzahl der Heimtore an.',
    validate: {
      isNumber
    },
    max: {
      value: (_initData$homeGoals2 = initData === null || initData === void 0 ? void 0 : initData.homeGoals) !== null && _initData$homeGoals2 !== void 0 ? _initData$homeGoals2 : match.homeGoal,
      message: 'Eingabe darf die Anzahl der Heimtore nicht übersteigen.'
    },
    min: {
      value: 0,
      message: 'Die Anzahl der Heimtore muss mindestens 0 sein.'
    }
  }), {
    "data-sentry-element": "CustomNumberTextField",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  })), /*#__PURE__*/React.createElement(Label, {
    variant: "body1",
    padding: 8,
    "data-sentry-element": "Label",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }, ":"), /*#__PURE__*/React.createElement(CustomNumberTextField, _extends({
    error: !!errors.awayGoals,
    type: "number",
    variant: "outlined",
    label: "Gast",
    size: "small",
    maxWidth: 52,
    textAlign: "center",
    inputProps: {
      inputMode: 'numeric'
    },
    InputLabelProps: {
      shrink: true
    }
  }, register('awayGoals', {
    required: 'Gib die Anzahl der Gasttore an.',
    validate: {
      isNumber
    },
    max: {
      value: (_initData$awayGoals2 = initData === null || initData === void 0 ? void 0 : initData.awayGoals) !== null && _initData$awayGoals2 !== void 0 ? _initData$awayGoals2 : match.awayGoal,
      message: 'Eingabe darf die Anzahl der Gasttore nicht übersteigen.'
    },
    min: {
      value: 0,
      message: 'Die Anzahl der Gasttore muss mindestens 0 sein.'
    }
  }), {
    "data-sentry-element": "CustomNumberTextField",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }))), matchEventId && onDelete ? /*#__PURE__*/React.createElement(DeleteScorerButton, {
    matchEventId: matchEventId,
    matchSlug: match.slug,
    onDelete: onDelete,
    onError: setDeletionError
  }) : /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement(SharedErrorContainer, {
    "data-sentry-element": "SharedErrorContainer",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }, getSharedError([errors.minute, errors.homeGoals, errors.awayGoals]))), /*#__PURE__*/React.createElement(GridElement, {
    "data-sentry-element": "GridElement",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }, /*#__PURE__*/React.createElement(CustomTextField, {
    select: true,
    error: !!errors.category,
    fullWidth: true,
    variant: "outlined",
    size: "small",
    label: "Torkategorie",
    id: "category-select",
    defaultValue: (_initData$category2 = initData === null || initData === void 0 ? void 0 : initData.category) !== null && _initData$category2 !== void 0 ? _initData$category2 : defaultInitData.category,
    helperText: (_errors$category$mess = (_errors$category = errors.category) === null || _errors$category === void 0 ? void 0 : _errors$category.message) !== null && _errors$category$mess !== void 0 ? _errors$category$mess : '',
    inputProps: register('category'),
    "data-sentry-element": "CustomTextField",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }, Object.entries(goalType).filter(_ref3 => {
    let [slug, _] = _ref3;
    return slug !== 'goal_freekick';
  }).map(_ref4 => {
    let [slug, label] = _ref4;
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: slug,
      value: slug
    }, label || 'Schuss');
  }))), /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement(GridElement, {
    "data-sentry-element": "GridElement",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }, /*#__PURE__*/React.createElement(Controller, {
    render: _ref5 => {
      var _errors$team$message, _errors$team;
      let {
        field
      } = _ref5;
      return /*#__PURE__*/React.createElement(CustomTextField, _extends({
        select: true,
        error: !!errors.team,
        fullWidth: true,
        variant: "outlined",
        size: "small",
        label: "Tor f\xFCr",
        id: "team-select",
        helperText: (_errors$team$message = (_errors$team = errors.team) === null || _errors$team === void 0 ? void 0 : _errors$team.message) !== null && _errors$team$message !== void 0 ? _errors$team$message : ''
      }, field), /*#__PURE__*/React.createElement(MenuItem, {
        disabled: !hasGoals(homeGoals),
        value: "homeTeam"
      }, match.homeTeamName), /*#__PURE__*/React.createElement(MenuItem, {
        disabled: !hasGoals(awayGoals),
        value: "awayTeam"
      }, match.awayTeamName));
    },
    control: control,
    rules: {
      required: 'Gib das Team an, das dieses Tor erzielt hat.'
    },
    name: "team",
    defaultValue: (_initData$team2 = initData === null || initData === void 0 ? void 0 : initData.team) !== null && _initData$team2 !== void 0 ? _initData$team2 : defaultInitData.team,
    "data-sentry-element": "Controller",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  })), /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement(PlayerSelect, {
    error: errors.scorer,
    defaultValue: (_initData$scorer2 = initData === null || initData === void 0 ? void 0 : initData.scorer) !== null && _initData$scorer2 !== void 0 ? _initData$scorer2 : defaultInitData.scorer,
    control: control,
    label: "Torsch\xFCtze",
    name: "scorer",
    players: relevantPlayers,
    show: !!(initData !== null && initData !== void 0 && initData.scorer || team),
    "data-sentry-element": "PlayerSelect",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }), /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement(PlayerSelect, {
    error: errors.assistant,
    defaultValue: (_initData$assistant2 = initData === null || initData === void 0 ? void 0 : initData.assistant) !== null && _initData$assistant2 !== void 0 ? _initData$assistant2 : defaultInitData.assistant,
    control: control,
    label: assistantLabel[category],
    name: "assistant",
    players: relevantAssistants,
    show: !!((initData !== null && initData !== void 0 && initData.scorer || scorer) && !isOwnGoal && assistantLabel[category]),
    "data-sentry-element": "PlayerSelect",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }), /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement(SharedErrorContainer, {
    "data-sentry-element": "SharedErrorContainer",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }, submissionError && /*#__PURE__*/React.createElement(ErrorTypography, {
    variant: "caption1"
  }, submissionError), deletionError && /*#__PURE__*/React.createElement(ErrorTypography, {
    variant: "caption1"
  }, deletionError)), /*#__PURE__*/React.createElement(ActionContainer, {
    "data-sentry-element": "ActionContainer",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    sx: {
      color: COLORS.darkestGrey
    },
    onClick: onClose,
    "data-sentry-element": "Button",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }, "Abbrechen"), /*#__PURE__*/React.createElement(LoadingButton, {
    variant: "contained",
    color: "secondary",
    onClick: handleSubmit(onValidationSuccess),
    loading: submissionLoading,
    "data-sentry-element": "LoadingButton",
    "data-sentry-source-file": "UpdateScorerForm.tsx"
  }, "Speichern")));
}